import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { PolicyConsumerData, PROPERTY_CONSTANTS } from '@nationwide/dgs-internet-servicing-policy-requests';
import { MortgageCompany, PropertyChangeRequest, PropertyRequestsOptions } from '../../../tpi-models/policy-requests';
import { AwdWorkflowServiceRequest } from '../../../tpi-models/general/awd-workflow-service-request';
import { EditMortgageServiceRequest, Mortgage, MortgageAddress } from '../../../tpi-models/general/edit-mortgage-services-request';
import { LienHolder } from '../../../tpi-models/general/lien-holder';
import { MortgageCompanyDetails } from '../../../tpi-models/general/mortgage-company';
import { commonConstants } from '../../../CONSTANTS/tpi-common-constants';

@Injectable({
    providedIn: 'root'
})
export class RequestMapperService {
    private datepipe = inject(DatePipe);

    buildAwdWorkflowAutoServiceRequest(lienholderRequest: LienHolder, vin: string): AwdWorkflowServiceRequest {
        const request: AwdWorkflowServiceRequest = {};
        request.policyNumber = lienholderRequest.policyNumber;
        request.vin = vin;
        request.changeEffectiveDate = lienholderRequest.changeEffectiveDate;
        request.loanOrLeaseDetails = lienholderRequest.loanOrLeaseDetails;
        request.lienholderDetails = lienholderRequest.additionalInterest;
        request.policyType = 'Auto';
        return request;
    }

    buildAwdWorkflowServiceRequest(editMortgageServiceRequest: EditMortgageServiceRequest): AwdWorkflowServiceRequest {
        const request: AwdWorkflowServiceRequest = {};
        request.loanNumber = editMortgageServiceRequest.loanNumber;
        request.policyNumber = editMortgageServiceRequest.policyNumber;
        request.policyType = editMortgageServiceRequest.lineOfBusiness;
        request.policyZipCode = editMortgageServiceRequest.policyZipCode;
        request.mortgageCompany = editMortgageServiceRequest.mortgageCompany;
        return request;
    }

    buildEditMortgageServiceRequqest(mortgageCompanyDetails: MortgageCompanyDetails): EditMortgageServiceRequest {
        const request: EditMortgageServiceRequest = {};
        request.policyNumber = mortgageCompanyDetails.policyNumber;
        request.effectiveDate = this.formatEffectiveDate(mortgageCompanyDetails.changeEffectiveDate);
        request.policyZipCode = mortgageCompanyDetails.zipCode;
        request.lineOfBusiness = 'PolicyCenter';
        request.policyType = this.mapProductType(mortgageCompanyDetails.propertyType);
        request.loanNumber = mortgageCompanyDetails.loanNumber;
        request.companyId = mortgageCompanyDetails?.mortgageCompanyAddress?.companyId;
        request.mortgageCompany = this.buildMortgageCompany(mortgageCompanyDetails);

        return request;
    }

    getApplyMortgagePropertyPolicyRequest(editMortgageServiceRequest: EditMortgageServiceRequest): PropertyRequestsOptions {
        const mortgage: MortgageCompany = {
            mortgageType: 'Mortgagee_Ext',
            type: 'Mortgagee1',
            loanNumber: editMortgageServiceRequest.loanNumber,
            companyId: editMortgageServiceRequest.companyId,
            companyName: editMortgageServiceRequest.mortgageCompany.companyName
        };
        const request: PropertyChangeRequest = {
            policyNumber: editMortgageServiceRequest.policyNumber,
            policyChangeId: editMortgageServiceRequest.policyChangeId,
            mortgageId: editMortgageServiceRequest.mortgageCompany.mortgageId,
            mortgage
        };
        return this.getPropertyRequestAction(editMortgageServiceRequest.policyType, request, true);
    }

    getConsumerData(): PolicyConsumerData {
        const consumerData: PolicyConsumerData = {
            accessToken: sessionStorage.getItem('jwt'),
            camSessionId: ''
        };
        return consumerData;
    }

    getDeleteMortgagePropertyPolicyRequest(editMortgageServiceRequest: EditMortgageServiceRequest): PropertyRequestsOptions {
        const request: PropertyChangeRequest = {
            policyNumber: editMortgageServiceRequest.policyNumber,
            policyChangeId: editMortgageServiceRequest.policyChangeId,
            mortgageId: editMortgageServiceRequest.mortgageCompany.mortgageId
        };
        return this.getPropertyRequestAction(editMortgageServiceRequest.policyType, request, true);
    }

    getRateOrSubmitPropertyPolicyChangeRequest(editMortgageServiceRequest: EditMortgageServiceRequest): PropertyRequestsOptions {
        const request: PropertyChangeRequest = {
            policyNumber: editMortgageServiceRequest.policyNumber,
            policyChangeId: editMortgageServiceRequest.policyChangeId
        };
        return this.getPropertyRequestAction(editMortgageServiceRequest.policyType, request, true);
    }

    getRetrieveAllMortgagesRequest(editMortgageServiceRequest: EditMortgageServiceRequest): PropertyRequestsOptions {
        const request: PropertyChangeRequest = {
            policyNumber: editMortgageServiceRequest.policyNumber
        };
        return this.getPropertyRequestAction(editMortgageServiceRequest.policyType, request, true);
    }

    getStartPolicyChangeRequest(editMortgageServiceRequest: EditMortgageServiceRequest): PropertyRequestsOptions {
        const request: PropertyChangeRequest = {
            policyNumber: editMortgageServiceRequest.policyNumber,
            effectiveDate: editMortgageServiceRequest.effectiveDate
        };
        return this.getPropertyRequestAction(editMortgageServiceRequest.policyType, request, true);
    }

    getwithdrawPolicyChangeRequest(editMortgageServiceRequest: EditMortgageServiceRequest): PropertyRequestsOptions {
        const request: PropertyChangeRequest = {
            policyChangeId: editMortgageServiceRequest.policyChangeId
        };
        return this.getPropertyRequestAction(editMortgageServiceRequest.policyType, request, true);
    }

    private buildMortgageAddress(mortgageCompanyDetails: MortgageCompanyDetails): MortgageAddress {
        const addressDetails = mortgageCompanyDetails?.mortgageCompanyAddress?.address;
        return {
            addressLine1: addressDetails?.addressLine1,
            city: addressDetails?.city,
            postalCode: addressDetails?.postalCode,
            state: addressDetails?.state
        };
    }

    private buildMortgageCompany(mortgageCompanyDetails: MortgageCompanyDetails): Mortgage {
        return {
            companyName: mortgageCompanyDetails?.mortgageCompanyAddress?.companyName || mortgageCompanyDetails?.mortgageCompanyAddress?.name,
            companyId: mortgageCompanyDetails?.mortgageCompanyAddress?.companyId,
            ediNumber: mortgageCompanyDetails?.mortgageCompanyAddress?.ediNumber,
            name: mortgageCompanyDetails?.mortgageCompanyAddress?.name,
            address: this.buildMortgageAddress(mortgageCompanyDetails)
        };
    }

    private formatEffectiveDate(date: string | Date): string | null {
        return this.datepipe.transform(date, 'yyyy-MM-dd');
    }

    private getPropertyRequestAction(propertyType?: string, request?: PropertyChangeRequest, skipCache = true): PropertyRequestsOptions {
        const propertyRequestsOptions: PropertyRequestsOptions = {
            propertyType,
            request,
            skipCache: true
        };
        return propertyRequestsOptions;
    }

    private mapProductType(productResponse: string): string {
        let productType = '';
        switch (productResponse) {
            case commonConstants.PROPERTY.PRODUCT_TYPE_HOMEOWNER:
            case 'Homeowner':
                productType = PROPERTY_CONSTANTS.HOME_OWNER;
                break;
            case commonConstants.PROPERTY.PRODUCT_TYPE_CONDO:
            case 'Condominium':
                productType = 'condominium';
                break;
            case commonConstants.PROPERTY.PRODUCT_TYPE_DWELLINGFIRE:
            case 'Dwelling fire':
                productType = 'dwelling-fire';
                break;
        }
        return productType;
    }
}